import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Wheat, Droplet, Sprout, CheckCircle } from 'lucide-react';

const Agricola = () => {
  const { t } = useLanguage();

  const capabilities = [
    { 
      icon: Wheat, 
      title: t.agricola.capabilities.items[0].title,
      description: t.agricola.capabilities.items[0].description
    },
    { 
      icon: Droplet, 
      title: t.agricola.capabilities.items[1].title,
      description: t.agricola.capabilities.items[1].description
    },
    { 
      icon: Sprout, 
      title: t.agricola.capabilities.items[2].title,
      description: t.agricola.capabilities.items[2].description
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 min-h-[60vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mea-connect/artifacts/gmhykz7f_banner%20pagina%20faina_golden%20sheaf.jpeg"
            alt="Golden Sheaf Premium Wheat Flour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#003840]/65 via-[#003840]/55 to-[#003840]/65"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-float">
              {t.agricola.title}
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed animate-fade-in">
              {t.agricola.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center space-y-6">
            {t.agricola.intro.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-lg text-gray-600 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">
              {t.agricola.product.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Product Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#F4F7F6] to-white rounded-3xl p-8 shadow-xl">
                <img
                  src={t.agricola.product.image}
                  alt={t.agricola.product.name}
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-[#003840] mb-2">
                  {t.agricola.product.name}
                </h3>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl font-bold text-[#007369]">{t.agricola.product.weight}</span>
                  <span className="text-gray-500">|</span>
                  <span className="text-lg text-gray-600">{t.agricola.product.subtitle}</span>
                </div>
              </div>

              <p className="text-gray-600 leading-relaxed text-lg">
                {t.agricola.product.description}
              </p>

              <div className="bg-[#F4F7F6] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#003840] mb-4">Key Features</h4>
                <ul className="space-y-3">
                  {t.agricola.product.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-[#007369] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distribution Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">
              {t.agricola.capabilities.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <div
                  key={index}
                  className="bg-[#F4F7F6] p-8 rounded-2xl hover:shadow-2xl transition-all duration-300"
                >
                  <div className="p-3 bg-white rounded-lg inline-block mb-4">
                    <Icon className="h-8 w-8 text-[#007369]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#003840] mb-3">{capability.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{capability.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003840] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t.cta.distribution.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.distribution.subtitle}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Agricola;
